import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash, FaSearch, FaUser, FaEnvelope, FaPhone, FaUserShield, FaUserTag } from 'react-icons/fa';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import './UserManager.css';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [filters, setFilters] = useState({
    role: '',
    search: '',
    page: 1,
    limit: 20
  });
  const [totalPages, setTotalPages] = useState(1);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.role) params.role = filters.role;
      if (filters.search) params.search = filters.search;
      params.page = filters.page;
      params.limit = filters.limit;

      const response = await axios.get(`${API_URL}/api/users`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      setUsers(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error('Users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data.data);
      setShowUserModal(true);
    } catch (error) {
      toast.error('Failed to fetch user details');
    }
  };

  const handleDeleteUser = (user) => {
    setConfirmModal({
      show: true,
      title: 'Delete User',
      message: `Are you sure you want to delete user "${user.username}" (${user.email})? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/users/${user._id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('User deleted successfully');
          fetchUsers();
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete user');
        }
      },
      type: 'danger'
    });
  };

  const handleUpdateUser = async (userId, updateData) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User updated successfully');
      fetchUsers();
      if (showUserModal) {
        handleViewUser(userId);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="user-manager">
        <div className="loading-container">
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-manager">
      <div className="user-header">
        <div>
          <h1>User Management</h1>
          <p className="subtitle">Manage users registered from the website</p>
        </div>
        <div className="user-filters">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              className="search-input"
            />
          </div>
          <select
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value, page: 1 })}
            className="filter-select"
          >
            <option value="">All Roles</option>
            <option value="user">Users</option>
            <option value="admin">Admins</option>
          </select>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Name</th>
              <th>Role</th>
              <th>Registered</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-users">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="user-info">
                      <strong>{user.username}</strong>
                    </div>
                  </td>
                  <td>
                    <div className="user-info">
                      <FaEnvelope className="info-icon" />
                      {user.email || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="user-info">
                      <FaPhone className="info-icon" />
                      {user.phone || user.profile?.phone || 'N/A'}
                    </div>
                  </td>
                  <td>
                    {user.profile?.fullName || user.profile?.firstName || 'N/A'}
                  </td>
                  <td>
                    <span className={`role-badge ${user.role === 'admin' ? 'admin' : 'user'}`}>
                      {user.role === 'admin' ? <FaUserShield /> : <FaUserTag />}
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {formatDate(user.createdAt)}
                  </td>
                  <td>
                    <div className="user-actions">
                      <button
                        onClick={() => handleViewUser(user._id)}
                        className="btn btn-sm btn-primary"
                        title="View Details"
                      >
                        <FaEdit /> View
                      </button>
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="btn btn-sm btn-danger"
                          title="Delete User"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            disabled={filters.page === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          <span>
            Page {filters.page} of {totalPages}
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            disabled={filters.page >= totalPages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {/* User Detail Modal */}
      {showUserModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content user-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={() => setShowUserModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-details-grid">
                <div className="user-section">
                  <h3>Account Information</h3>
                  <div className="detail-item">
                    <strong>Username:</strong> {selectedUser.username}
                  </div>
                  <div className="detail-item">
                    <strong>Email:</strong> {selectedUser.email || 'N/A'}
                  </div>
                  <div className="detail-item">
                    <strong>Phone:</strong> {selectedUser.phone || selectedUser.profile?.phone || 'N/A'}
                  </div>
                  <div className="detail-item">
                    <strong>Role:</strong>
                    <select
                      value={selectedUser.role}
                      onChange={(e) => handleUpdateUser(selectedUser._id, { role: e.target.value })}
                      className="role-select"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>

                <div className="user-section">
                  <h3>Profile Information</h3>
                  <div className="detail-item">
                    <strong>Full Name:</strong> {selectedUser.profile?.fullName || selectedUser.profile?.firstName || 'Not set'}
                  </div>
                  {selectedUser.profile?.address && (
                    <div className="detail-item">
                      <strong>Address:</strong>
                      <div className="address-block">
                        {selectedUser.profile.address.street && (
                          <div>{selectedUser.profile.address.street}</div>
                        )}
                        {(selectedUser.profile.address.city || selectedUser.profile.address.state) && (
                          <div>
                            {selectedUser.profile.address.city}{selectedUser.profile.address.city && selectedUser.profile.address.state ? ', ' : ''}
                            {selectedUser.profile.address.state}
                          </div>
                        )}
                        {selectedUser.profile.address.zipCode && (
                          <div>{selectedUser.profile.address.zipCode}</div>
                        )}
                        {selectedUser.profile.address.country && (
                          <div>{selectedUser.profile.address.country}</div>
                        )}
                      </div>
                    </div>
                  )}
                  {selectedUser.profile?.dateOfBirth && (
                    <div className="detail-item">
                      <strong>Date of Birth:</strong> {formatDate(selectedUser.profile.dateOfBirth)}
                    </div>
                  )}
                  {selectedUser.profile?.gender && (
                    <div className="detail-item">
                      <strong>Gender:</strong> {selectedUser.profile.gender}
                    </div>
                  )}
                </div>

                <div className="user-section">
                  <h3>Account Details</h3>
                  <div className="detail-item">
                    <strong>User ID:</strong> {selectedUser._id}
                  </div>
                  <div className="detail-item">
                    <strong>Registered:</strong> {formatDate(selectedUser.createdAt)}
                  </div>
                  {selectedUser.phoneVerified && (
                    <div className="detail-item">
                      <strong>Phone Verified:</strong> ✓ Yes ({formatDate(selectedUser.phoneVerifiedAt)})
                    </div>
                  )}
                  {selectedUser.firebaseUid && (
                    <div className="detail-item">
                      <strong>Firebase Auth:</strong> Enabled
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowUserModal(false)} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' })}
        confirmText="Delete User"
        cancelText="Cancel"
        type={confirmModal.type}
      />
    </div>
  );
};

export default UserManager;

